import { Button } from '@chakra-ui/button';
import { InputGroup } from '@chakra-ui/input';
import { Flex, Heading, Spacer } from '@chakra-ui/layout';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseDirectoryFiles } from '../utils/files';

interface Props {
  onChange: (f: FileList) => void;
  title: string;
  accept?: string;
  multiple?: boolean;
  isLoading?: boolean;
  /** Allow uploading folders. @default true */
  directory?: boolean;
}

const FileUploadArea = ({ onChange, accept, multiple, title, isLoading, directory }: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const directoryProps = directory ? {
    webkitdirectory: true,
    mozdirectory: true,
    msdirectory: true,
    odirectory: true,
    directory: true,
  } : {};

  const handleClick = () => inputRef.current?.click();

  return (
    <InputGroup
      className='file-upload-area'
      onClick={handleClick}
      // handle drag and drop
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.items;
        if (files.length > 0 && files[0].kind === 'file') {
          setIsDraggedOver(true);
        }
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggedOver(false);
      }}
      // onDragOver={(e) => e.preventDefault()}
      onDrop={async (e) => {
        const isValidFile = (fileName: string, kind: string) => {
          const matchesAccept = accept?.split(',').some((ext) => fileName.endsWith(ext) || kind === accept) || false;
          return matchesAccept;
        }
        e.preventDefault();
        setIsDraggedOver(false);
        const items = e.dataTransfer.items;
        const fileList = new DataTransfer();
        const filePromises = [];
        for (const item of Array.from(items)) {
          const i = item.webkitGetAsEntry();

          // Extract this handling somewhere
          if (i?.isDirectory) {
            const fileSystemEntries = (await parseDirectoryFiles(i));
            for (const f of fileSystemEntries) {
              if (isValidFile(f.name, item.kind)) {
                const filePromise = new Promise<void>((resolve) => {
                  f.file((fileObj) => {
                    fileList.items.add(fileObj);
                    resolve();
                  });
                });
                filePromises.push(filePromise);
              }
            }
          } else {
            const f = item.getAsFile();
            if (!(f && isValidFile(f.name, item.kind))) continue;
            fileList.items.add(f);
          }
        }
        await Promise.all(filePromises);
        onChange(fileList.files);
      }}
    >
      <input
        type='file'
        multiple={multiple || false}
        hidden
        accept={accept}
        onChange={(e) => onChange(e.target.files as FileList)}
        ref={(e) => inputRef.current = e}
        {...directoryProps}
      />
      <>
        <Flex
          width='100%'
          minHeight='10rem'
          padding='1rem'
          borderRadius='md'
          justifyContent='center'
          gap='1rem'
          cursor='pointer'
          color={isDraggedOver ? '#000' : '#fff'}
          borderWidth='2px'
          borderStyle={isDraggedOver ? 'dashed' : 'solid'}
          borderColor={isDraggedOver ? '#ccc' : '#fff'}
          backgroundColor={isDraggedOver ? '#fff' : 'inherit'}
          _hover={{
            color: '#000',
            backgroundColor: '#fff',
          }}
          transition='all 0.2s ease-in'
          className='file-upload-area'
          textTransform='none'
          flexDirection={{ base: 'column', lg: 'row' }}
        >
          <Spacer />
          <Heading
            size='md'
            fontWeight='medium'
            marginY='auto'
          >
            {title} {t('or')}
          </Heading>
          <Spacer />
          <Button
            colorScheme='primary'
            alignSelf='center'
            isLoading={isLoading}
            isDisabled={isLoading}
            loadingText={t('loading')!}
          >
            {t('browse')}
          </Button>
        </Flex>
      </>
    </InputGroup>
  )
}

export default FileUploadArea;